import { portfolioClaimSignUpMetadata } from '@/features/portfolio/_utils/portfolio-claim-origin';
import {
  flattenPortfolioAttribution,
  type PortfolioAttribution,
} from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import type { ProvisionEndpoint } from '@/infra/constants/provision';
import { getSignupProvisionPayloadKey, SIGNUP_PROVISION_PAYLOAD_KEY } from '@/infra/constants/routes';
import { AUTH_EVENTS } from '@/lib/analytics/auth-events';
import { getAnonymousCorrelationId } from '@/lib/analytics/get-anonymous-correlation-id';

type AccountCreationMethod = 'email' | 'google';

interface IdentifyAuthUserPayload {
  accountCreationMethod: AccountCreationMethod;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  newsletter: boolean;
  userId?: string;
}

interface CompletionTracking {
  identifyAuthUser: (payload: IdentifyAuthUserPayload) => void;
  onCompleted?: () => void;
  track: (eventName: string, properties?: Record<string, unknown>) => void;
}

interface CompletionOptions {
  accountCreationMethod: AccountCreationMethod;
  allowlistedParams: string;
  campaign: string;
  clearStoredAccountCreationMethod: () => void;
  clearStoredNewsletter: () => void;
  email: string;
  /**
   * The Clerk user's `unsafeMetadata` as it already stands. Provisioning replaces that object
   * wholesale, so whatever another app wrote there only survives by riding along in the payload.
   * Left unset by the flows that create the account themselves, where there is nothing to keep.
   */
  existingUnsafeMetadata?: Record<string, unknown>;
  firstName: string;
  freemiumVariant?: string;
  lastName: string;
  medium: string;
  newsletter: boolean;
  partner: string;
  portfolioAttribution?: PortfolioAttribution | null;
  /** Left unset for sign-ups this app runs itself, which provision through the default endpoint. */
  provisionEndpoint?: ProvisionEndpoint;
  redirect: () => void;
  referrer: string;
  source: string;
  tracking: CompletionTracking;
  userId: string;
  /**
   * `utm_content` as `useUtmParams` resolved it — URL first, stored value second, so it survives the
   * OAuth hop that strips the query string. Only its claim-flow values are read, to mark an account
   * that arrives adopting a portfolio it built before it existed.
   */
  utmContent: string;
}

/**
 * The whole of Clerk's `unsafeMetadata` after provisioning: the endpoint replaces the object rather
 * than merging into it. The index signature is what lets keys owned elsewhere — the website sign-up
 * quiz, Labs entry state — pass through instead of being dropped on the way.
 */
interface ProvisionUnsafeMetadata {
  account_creation_method: AccountCreationMethod;
  newsletter: boolean;
  portfolio_attribution?: PortfolioAttribution;
  /** Absent for every arrival that did not come from a claimed portfolio page. */
  portfolio_claimed_at_sign_up?: true;
  search_params: string;
  utm_campaign: string;
  utm_medium: string;
  utm_referrer: string;
  utm_source: string;
  [key: string]: unknown;
}

function buildProvisionPayload(options: {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  newsletter: boolean;
  accountCreationMethod: AccountCreationMethod;
  allowlistedParams: string;
  campaign: string;
  medium: string;
  source: string;
  referrer: string;
  partner: string;
  portfolioAttribution?: PortfolioAttribution | null;
  provisionEndpoint?: ProvisionEndpoint;
  freemiumVariant?: string;
  existingUnsafeMetadata?: Record<string, unknown>;
  anonymousId: string;
  utmContent: string;
}): {
  userId: string;
  email: string;
  fullName: string;
  anonymousId: string;
  provisionEndpoint?: ProvisionEndpoint;
  referralCode: string;
  publicMetadata: {
    role: 'APPLICANT';
    partner?: string;
    ab_test_freemium?: string;
  };
  unsafeMetadata: ProvisionUnsafeMetadata;
} {
  const {
    userId,
    email,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
    allowlistedParams,
    campaign,
    medium,
    source,
    referrer,
    partner,
    portfolioAttribution,
    provisionEndpoint,
    freemiumVariant,
    existingUnsafeMetadata,
    anonymousId,
    utmContent,
  } = options;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    userId,
    email,
    fullName,
    anonymousId: anonymousId ?? '',
    ...(provisionEndpoint ? { provisionEndpoint } : {}),
    referralCode: referrer,
    publicMetadata: {
      role: 'APPLICANT',
      ...(partner && { partner }),
      ...(freemiumVariant !== undefined && { ab_test_freemium: freemiumVariant }),
    },
    unsafeMetadata: {
      // Spread first so the keys this sign-up owns win over a stale copy of themselves.
      ...existingUnsafeMetadata,
      newsletter,
      utm_medium: medium,
      utm_source: source,
      utm_campaign: campaign,
      utm_referrer: referrer,
      search_params: allowlistedParams,
      account_creation_method: accountCreationMethod,
      ...(portfolioAttribution ? { portfolio_attribution: portfolioAttribution } : {}),
      // Provisioning writes this object back over Clerk's, so the marker has to ride the payload.
      ...portfolioClaimSignUpMetadata(utmContent),
    },
  };
}

function runPostProvisionTracking(options: {
  tracking: CompletionTracking;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  newsletter: boolean;
  accountCreationMethod: AccountCreationMethod;
  campaign: string;
  medium: string;
  source: string;
  referrer: string;
  partner: string;
  portfolioAttribution?: PortfolioAttribution | null;
}): void {
  const {
    tracking,
    userId,
    email,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
    campaign,
    medium,
    source,
    referrer,
    partner,
    portfolioAttribution,
  } = options;

  const attributionProps = flattenPortfolioAttribution(portfolioAttribution);

  tracking.identifyAuthUser({
    userId,
    email,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
  });

  tracking.track('gads_user_signed_up', {
    utm_campaign: campaign,
    utm_medium: medium,
    utm_source: source,
    utm_referrer: referrer,
    ...(partner && { partner }),
    ...attributionProps,
  });

  tracking.track('application_created', {
    ...attributionProps,
  });
  tracking.track('gads_application_created', {
    ...attributionProps,
  });
  tracking.onCompleted?.();
}

export function completeSignUp(options: CompletionOptions): void {
  const {
    email,
    userId,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
    allowlistedParams,
    campaign,
    medium,
    source,
    referrer,
    partner,
    portfolioAttribution,
    provisionEndpoint,
    freemiumVariant,
    existingUnsafeMetadata,
    utmContent,
    tracking,
    clearStoredNewsletter,
    clearStoredAccountCreationMethod,
    redirect,
  } = options;

  const completionStartedAt = performance.now();
  const provisionPayload = buildProvisionPayload({
    email,
    userId,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
    allowlistedParams,
    campaign,
    medium,
    source,
    referrer,
    partner,
    portfolioAttribution,
    provisionEndpoint,
    freemiumVariant,
    existingUnsafeMetadata,
    utmContent,
    anonymousId: getAnonymousCorrelationId(),
  });

  localStorage.setItem(getSignupProvisionPayloadKey(userId), JSON.stringify(provisionPayload));
  localStorage.removeItem(SIGNUP_PROVISION_PAYLOAD_KEY);
  const afterStorePayloadAt = performance.now();

  runPostProvisionTracking({
    tracking,
    userId,
    email,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
    campaign,
    medium,
    source,
    referrer,
    partner,
    portfolioAttribution,
  });
  const afterTrackingAt = performance.now();

  tracking.track(AUTH_EVENTS.SIGN_UP_COMPLETION_STAGE_TIMING, {
    account_creation_method: accountCreationMethod,
    store_payload_ms: Math.round(afterStorePayloadAt - completionStartedAt),
    run_tracking_ms: Math.round(afterTrackingAt - afterStorePayloadAt),
    total_ms_before_redirect_ms: Math.round(afterTrackingAt - completionStartedAt),
  });

  clearStoredNewsletter();
  clearStoredAccountCreationMethod();

  tracking.track(AUTH_EVENTS.SIGN_UP_REDIRECT_STARTED, {
    account_creation_method: accountCreationMethod,
  });

  redirect();
}
